import { Head, Link, router } from "@inertiajs/react"
import { Activity as ActivityIcon } from "lucide-react"
import { useState } from "react"

import { Breadcrumbs } from "@/components/breadcrumbs"
import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface ActivityEntry {
  id: number
  case_id: number
  activity_type: string
  subtype: string | null
  activity_date: string | null
  user_name: string | null
  description: string | null
  detail_path: string | null
}

interface Employee {
  id: number
  name: string
}

interface Filters {
  start_date: string
  end_date: string
  employee_id: string
}

interface Props {
  case_record: CaseRecord
  activities: ActivityEntry[]
  employees: Employee[]
  filters: Filters
}

/* -- Helpers ---------------------------------------------- */

function formatDate(date: string | null): string {
  return (
    formatDateUtil(date, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }) || "-"
  )
}

function typeLabel(row: ActivityEntry): string {
  if (row.subtype) return `${row.activity_type}/${row.subtype}`
  return row.activity_type
}

/* -- Columns ---------------------------------------------- */

const activityColumns: HpiColumn<ActivityEntry>[] = [
  {
    header: "Date",
    width: "max-w-[110px]",
    render: (row) => (
      <span className="text-xs font-medium">
        {formatDate(row.activity_date)}
      </span>
    ),
  },
  {
    header: "Employee",
    width: "max-w-[160px]",
    render: (row) => <span className="text-xs">{row.user_name ?? "-"}</span>,
  },
  {
    header: "Type",
    width: "max-w-[180px]",
    render: (row) => (
      <span className="text-xs font-medium">{typeLabel(row)}</span>
    ),
  },
  {
    header: "Description",
    render: (row) => <span className="text-xs">{row.description ?? "-"}</span>,
  },
]

/* -- Component -------------------------------------------- */

export default function CaseActivity({
  case_record,
  activities,
  employees,
  filters,
}: Props) {
  const c = case_record

  const [startDate, setStartDate] = useState(filters.start_date)
  const [endDate, setEndDate] = useState(filters.end_date)
  const [employeeId, setEmployeeId] = useState(filters.employee_id)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    { title: c.display_name || c.case_number, href: `/cases/${c.id}` },
    { title: "Activity", href: `/cases/${c.id}/activity` },
  ]

  const showFilters =
    activities.length > 10 || startDate || endDate || employeeId

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (employeeId) params.employee_id = employeeId
    router.get(`/cases/${c.id}/activity`, params, { preserveState: true })
  }

  return (
    <AppLayout>
      <Head title={`${c.display_name} - Activity`} />

      <div className="p-6">
        <div className="mb-6">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>

        <div className="mb-4 flex items-center gap-3">
          <ActivityIcon className="text-hpi-dark-blue h-5 w-5" />
          <h1 className="text-hpi-dark-gray text-xl font-semibold uppercase">
            {c.display_name} - Activity
          </h1>
        </div>

        {showFilters && (
          <form
            onSubmit={handleSearch}
            className="mb-6 flex flex-wrap items-end gap-4"
          >
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
                Employee
              </label>
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              >
                <option value="">All</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm font-medium text-white transition-colors"
            >
              Search
            </button>
          </form>
        )}

        <HpiDataTable<ActivityEntry>
          title="Activity"
          columns={activityColumns}
          data={activities}
          keyFn={(row) => `${row.activity_type}-${row.id}-${row.activity_date}`}
          rowHref={(row) => row.detail_path ?? undefined}
        />

        {activities.length === 0 && (
          <div className="text-hpi-med-gray py-8 text-center text-sm">
            No activity found for this case.
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${c.id}`}
            className="text-hpi-active-blue text-sm hover:underline"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
